import PropTypes from 'prop-types';
import { useRef } from 'react';
import { Fade, makeStyles } from '@material-ui/core';
import { TransitionGroup } from 'react-transition-group';
import { NavLink } from 'react-router-dom';
import classNames from 'classnames';
import DraggableScroll from '../draggableScroll';
import ScrollIndicators from '../scrollIndicators';
import { useDraggableScrollPosition } from '../../../utility/hooks';
import { asyncDebounce } from '../../../utility/helpers';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    boxSizing: 'border-box',
    backgroundColor: primary.white,
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%',
    maxWidth: '100%',
    paddingBottom: spacing(6),
  },
  nav: {
    borderBottom: `1px solid ${primary.bluish7}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 41,
    width: '100%',
    maxWidth: '100%',
  },
  links: {
    boxSizing: 'border-box',
    display: 'flex',
    height: 40,
    width: 'auto',
  },
  navItems: {
    display: 'flex',
  },
  navItem: {
    boxSizing: 'border-box',
    backgroundColor: 'transparent',
    borderBottom: '4px solid transparent',
    textDecoration: 'none',
    lineHeight: '20px',
    fontSize: '16px',
    color: primary.bluish1,
    fontFamily: 'ProximaNova-Bold',
    textTransform: 'uppercase',
    letterSpacing: 1,
    paddingBottom: spacing(2),
    paddingTop: spacing(2),
    marginRight: spacing(6),
    '&:focus-visible': {
      outline: 'none',
    },
    '&.active': {
      borderBottom: `4px solid ${primary.blue1}`,
    },
  },
  navItemCapitalized: {
    textTransform: 'capitalize',
    marginLeft: spacing(6),
    whiteSpace: 'nowrap',
    letterSpacing: 0,
  },
  scrollWrapper: {
    display: 'flex',
    height: 'auto',
  },
}));

const HorizontalNavigation = ({
  navItems,
  className = undefined,
  scrollArrowClass = undefined,
  customNavItemClass = undefined,
}) => {
  const classes = useStyles();
  const scrollRef = useRef(null);
  const itemsRef = useRef(null);
  const [
    activeScroll,
    onChangeScrollPosition,
    onScrollToRight,
    onScrollToBottom,
    onScrollToStart,
  ] = useDraggableScrollPosition(scrollRef, itemsRef);

  return (
    <div className={classNames(classes.root, className)}>
      <div className={classes.nav}>
        <DraggableScroll
          ref={el => {
            scrollRef.current = el;
          }}
          className={classNames(classes.scrollWrapper)}
          onEndScroll={asyncDebounce(onChangeScrollPosition)}
        >
          <div ref={itemsRef} className={classes.links}>
            <TransitionGroup className={classes.navItems}>
              {navItems.map(navItem => {
                return (
                  <Fade key={navItem.key} in unmountOnExit>
                    <NavLink
                      className={classNames(
                        classes.navItem,
                        {
                          [classes.navItemCapitalized]: navItem.isCapitalized,
                        },
                        customNavItemClass
                      )}
                      to={navItem.url}
                      end={navItem.end}
                    >
                      {navItem.name}
                    </NavLink>
                  </Fade>
                );
              })}
            </TransitionGroup>
          </div>
        </DraggableScroll>
        <ScrollIndicators
          arrowLeftClass={scrollArrowClass}
          arrowRightClass={scrollArrowClass}
          activeScroll={activeScroll}
          onScrollToBottom={onScrollToBottom}
          onScrollToRight={onScrollToRight}
          onScrollToStart={onScrollToStart}
        />
      </div>
    </div>
  );
};

HorizontalNavigation.propTypes = {
  className: PropTypes.string,
  scrollArrowClass: PropTypes.string,
  customNavItemClass: PropTypes.string,
  navItems: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default HorizontalNavigation;
